// TQ #8133: Defensiv-Hardening gegen "weisses Fenster nach Login".
//
// Zweck: Jeder Fehler, der NICHT von einer React-ErrorBoundary gefangen wird
// (also asynchrone Fehler, Promise-Rejections, Fehler ausserhalb des Render-
// Trees wie im Auth-/Bootstrap-Pfad), wird hier zentral abgefangen, strukturiert
// geloggt und best-effort an einen optionalen Telemetrie-Endpoint gemeldet.
//
// HINTERGRUND: Ein externer Nutzer meldete "nach dem Login nur ein weisses
// Fenster". Der Crash war nicht reproduzierbar (anonyme Session, kein Console-
// Fehler durchgereicht). Damit der NAECHSTE Fall sofort diagnostizierbar ist,
// loggen wir mit einem klaren, durchsuchbaren Marker und zeigen die Meldung im
// ErrorBoundary-Fallback an, damit ein Melder den Text durchgeben kann.
//
// SCOPE (Patric-Go 2026-06-16): KEIN neuer Backend-Endpoint. Wir NUTZEN nur
// einen Endpoint, falls per ENV (VITE_ERROR_TELEMETRY_URL) konfiguriert.
// Andernfalls reicht das prominente console.error + die Fallback-Anzeige.

/** Durchsuchbarer Marker fuer Logs/Console (Support kann danach grep'en). */
export const WHITE_SCREEN_MARKER = '[white-screen-guard]'

/**
 * Optionaler Telemetrie-Endpoint. Wird NUR genutzt, wenn per ENV gesetzt.
 * Es gibt heute KEINEN generischen Frontend-Error-Endpoint in der Plattform
 * (telemetry-service /api/v1/sync ist license-key-authentifiziert und auf das
 * AI-Desktop-Sync-Schema festgelegt) — deshalb defaulten wir auf "kein POST".
 */
const TELEMETRY_URL: string | undefined =
  (import.meta.env.VITE_ERROR_TELEMETRY_URL as string | undefined) || undefined

/** Strukturierter Error-Report. Bewusst flach + JSON-serialisierbar. */
export interface ErrorReport {
  marker: string
  source: 'window.onerror' | 'unhandledrejection' | 'react-error-boundary'
  message: string
  stack?: string
  url: string
  hostname: string
  userAgent: string
  /** Best-effort Kontext (Mode/User), falls verfuegbar — nie crashen. */
  mode?: string
  userId?: string
  timestamp: string
}

/**
 * Best-effort Kontext-Anreicherung. Liest NUR aus DOM/Globals, die immer da
 * sind. Darf selbst NIEMALS werfen (sonst frisst der Error-Handler den Error).
 */
function safeContext(): { mode?: string; userId?: string } {
  const ctx: { mode?: string; userId?: string } = {}
  try {
    const host = window.location.hostname
    if (host === 'with.it-sce.de') ctx.mode = 'personal'
    else if (host === 'cockpit.it-sce.de' || host === 'org.it-sce.de') ctx.mode = 'staff/org'
    else ctx.mode = 'custom-domain'
  } catch {
    /* ignore */
  }
  try {
    // userId nur falls eine spaetere Schicht ihn freiwillig hinterlegt hat.
    // (Wir setzen ihn NICHT selbst aus dem Token — kein PII-Leak by default.)
    const uid = (window as unknown as { __itsceUserId?: string }).__itsceUserId
    if (typeof uid === 'string' && uid) ctx.userId = uid
  } catch {
    /* ignore */
  }
  return ctx
}

/**
 * Setzt eine optionale User-Kennung fuer kuenftige Reports. Kann z.B. nach
 * erfolgreichem Auth gesetzt werden. Best-effort, wirft nie.
 */
export function setTelemetryUserId(userId: string | undefined): void {
  try {
    ;(window as unknown as { __itsceUserId?: string }).__itsceUserId = userId
  } catch {
    /* ignore */
  }
}

/**
 * Loggt + meldet einen Error. Wird sowohl von den globalen Handlern als auch
 * von der ErrorBoundary (componentDidCatch) aufgerufen. Best-effort: alles in
 * try/catch, nichts darf den aufrufenden Kontext crashen.
 */
export function reportError(
  source: ErrorReport['source'],
  error: unknown,
  extraStack?: string,
): void {
  let report: ErrorReport
  try {
    const err = error instanceof Error ? error : undefined
    const message =
      err?.message ??
      (typeof error === 'string' ? error : safeStringify(error)) ??
      'Unbekannter Fehler'
    const ctx = safeContext()
    report = {
      marker: WHITE_SCREEN_MARKER,
      source,
      message,
      stack: err?.stack ?? extraStack,
      url: tryGet(() => window.location.href, ''),
      hostname: tryGet(() => window.location.hostname, ''),
      userAgent: tryGet(() => navigator.userAgent, ''),
      mode: ctx.mode,
      userId: ctx.userId,
      timestamp: new Date().toISOString(),
    }
  } catch {
    // Selbst die Report-Konstruktion ist abgesichert.
    return
  }

  // 1) IMMER: prominentes, durchsuchbares Console-Log.
  try {
    // eslint-disable-next-line no-console
    console.error(
      `${WHITE_SCREEN_MARKER} ${report.source}: ${report.message}`,
      report,
    )
  } catch {
    /* ignore */
  }

  // 2) BEST-EFFORT: POST an optionalen Endpoint (nur wenn ENV gesetzt).
  if (TELEMETRY_URL) {
    try {
      const body = JSON.stringify(report)
      // sendBeacon ist non-blocking + ueberlebt Page-Unload — ideal fuer
      // den Fall "App crasht direkt nach Login". Kein await, kein throw.
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([body], { type: 'application/json' })
        navigator.sendBeacon(TELEMETRY_URL, blob)
      } else if (typeof fetch === 'function') {
        // Fallback: fire-and-forget fetch, Fehler explizit verschluckt.
        void fetch(TELEMETRY_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
          keepalive: true,
        }).catch(() => {
          /* best-effort, ignore */
        })
      }
    } catch {
      /* best-effort, ignore */
    }
  }
}

/**
 * Registriert die globalen Browser-Error-Handler. Idempotent (mehrfacher Aufruf
 * registriert nur einmal). Frueh im App-Entry aufrufen, damit auch Bootstrap-
 * Fehler vor dem ersten Render erfasst werden.
 */
let installed = false
export function installGlobalErrorHandlers(): void {
  if (installed) return
  installed = true
  try {
    window.addEventListener('error', (event: ErrorEvent) => {
      // event.error ist oft das echte Error-Objekt; Fallback auf message.
      reportError('window.onerror', event.error ?? event.message)
    })
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
      reportError('unhandledrejection', event.reason)
    })
  } catch {
    /* falls window nicht verfuegbar (SSR/Test) — ignorieren */
  }
}

// --- kleine Helfer, alle wurf-sicher ----------------------------------------

function tryGet<T>(fn: () => T, fallback: T): T {
  try {
    return fn()
  } catch {
    return fallback
  }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}
